<template>
  <div class="search-bar">
    <pre v-if="false" class="search-bar__debugger">{{ options }}</pre>
    <InputBox v-if="options.airport" class="search-bar__input-box" icon="v"
      :label="selectedAirportLabel" :value="selectedAirportValue"
      :highlight="dropdowns.airport"
      @click="toggle('airport')">
      <Airport v-if="dropdowns.airport"
        :value="options.airport" :allSelected="allAirportsSelected"
        @close="endEditAirport" @allSelected="allAirportsToggleSelected" />
    </InputBox>

    <DatesInputBox v-if="options.dates" class="search-bar__input-box" ref="datesInputBox"
      :label="selectedDateLabel"
      :start="selectedDateStartValue" :end="selectedDateEndValue"
      :highlight="dropdowns.dates ? options.dates.mode : ''"
      @click="startEditDates">

      <Calendar v-if="dropdowns.dates" ref="calendar"
        v-model="options.dates"
        :validDates="options.dates.validDates"
        :ranges="options.dates.ranges"
        :validRanges="options.dates.validRanges"
        :autoCloseParent="$refs.datesInputBox.$el"
        @updateEditedDates="updateEditedDates"
        @close="endEditDates"
        @input="updateAvailableValues" />
    </DatesInputBox>

    <InputBox v-if="options.duration" class="search-bar__input-box" icon="v"
      :label="selectedDurationLabel" :value="selectedDurationValue"
      :highlight="dropdowns.duration"
      @click="toggle('duration')">
      <RadioList v-if="dropdowns.duration" v-model="options.duration.range"
        @close="toggle('duration')"
        @input="updateAvailableValues"
        :options="options.duration.ranges" />
    </InputBox>

    <InputBox v-if="options.people" class="search-bar__input-box" icon="v" ref="peopleInput"
      label="" :value="selectedPeopleValue"
      :highlight="dropdowns.people"
      @click="toggle('people')">
      <People v-if="dropdowns.people" :value="options.people" @close="endEditPeople" />
    </InputBox>

    <InputBox v-if="options.room" class="search-bar__input-box" icon="v"
      :label="selectedRoomLabel" :value="selectedRoomValue"
      :highlight="dropdowns.room"
      @click="toggle('room')">
      <RadioList v-if="dropdowns.room" v-model="options.room.selected"
        @close="toggle('room')"
        @input="updateAvailableValues"
        :options="options.room.options" />
    </InputBox>

    <InputBox v-if="options.boarding" class="search-bar__input-box" icon="v"
      :label="selectedBoardingLabel" :value="selectedBoardingValue"
      :highlight="dropdowns.boarding"
      @click="toggle('boarding')">
      <RadioList v-if="dropdowns.boarding" v-model="options.boarding.selected"
        @close="toggle('boarding')"
        @input="updateAvailableValues"
        :options="options.boarding.options" />
    </InputBox>

    <InputBox v-if="options.pricePerPerson" class="search-bar__input-box" icon="v"
      :label="selectedPricePerPersonLabel" :value="selectedPricePerPersonValue"
      :highlight="dropdowns.pricePerPerson"
      @click="toggle('pricePerPerson')">
      <RadioList v-if="dropdowns.pricePerPerson" v-model="options.pricePerPerson.selected"
        @close="toggle('pricePerPerson')"
        @input="updateAvailableValues"
        :options="options.pricePerPerson.options" />
    </InputBox>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import moment from 'moment'
import Vue from 'vue'
import Component from 'vue-class-component'

import DatesInputBox from '@/components/DatesInputBox'
import DropdownHandler from '@/components/search/DropdownHandler'
import Airport from '@/components/search/fields/AirportDropdown'
import Calendar from '@/components/search/fields/CalendarDropdown'
import People from '@/components/search/fields/PeopleDropdown'
import RadioList from '@/components/search/fields/RadioListDropdown'
import InputBox from '@/components/search/InputBox'
import SelectLabelFormatter from '@/components/search/SelectLabelFormatter'

// The expected "value" for this component has the following structure
// which also determines which controls will be available for the user
//
// {
//   airport: Array of objects { name: String, value: String, enabled: Boolean, selected: Boolean },
//   dates: {
//     == required ==
//     mode: String of the following values: 'start', 'end', 'date'
//     selected: Moment.Range,
//     range: String in the format '1:5' - see 'ranges' field
//     == optional ==
//     validDates: Moment.Range,
//     ranges: Array of String in the following format: [ '1:5', '6:9', ... ]
//     validRanges: Array of String in the following format: [ '1:5', '6:9', ... ]
//   },
//   people: {
//     adults: { min: Number, max: Number, value: Number },
//     children: { min: Number, max: Number, value: Number },
//     childrenBirthdays: Array of Moment,
//     checkChildrenBirthday: Boolean (this is to steer the validation of children birthdays heaving default value)
//   },
//   room: {
//     selected: String,
//     options: Array of objects { name: String, value: String }
//   },
//   boarding: {
//     selected: String,
//     options: Array of objects { name: String, value: String }
//   },
//   pricePerPerson: {
//     selected: String,
//     options: Array of objects { name: String, value: String }
//   },
// }

@Component({
  mixins: [
    DropdownHandler,
    SelectLabelFormatter,
  ],
  components: {
    InputBox,
    Airport,
    DatesInputBox,
    Calendar,
    People,
    RadioList,
  },
  props: {
    value: {
      type: Object, // See above for the specification of the value object
      required: true,
    },
    enabled: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  watch: {
    value: {
      handler (newValue) {
        // When the value being edited changes from above, let's say after
        // the available options have been updated from an Ajax request
        // recreate internal state
        // This also happens when the 'input' event is sent from the Search
        // component which results in one additional recreation of options
        // but the overhead is completely acceptable
        this.options = cloneDeep(newValue)
      },
      deep: true,
    },
  },
})
export default class Search extends Vue {
  options = cloneDeep(this.value)

  // The following is a list of supported dropdown editors and their visibility initial state
  dropdowns = {
    airport: false,
    dates: false,
    duration: false,
    people: false,
    room: false,
    boarding: false,
    pricePerPerson: false,
  }

  updateAvailableValues () {
    if (!isEqual(this.options, this.value)) {
      this.$emit('input', this.options)
    }
  }

  getFormattedSelectLabel (item, label) {
    if (item.length === 0) return ''
    else return label
  }
  getFormattedSelectValue (item, label, max = 1) {
    if (item == null) return label
    else if (item.length === 0) return label
    else if (typeof item === 'string' && item != '') return item
    else if (item.length <= max) return item.take(item.length).map(x => x.name).join(', ')
    else return `${item.length} wybrano`
  }

  // Airports --------------
  allAirportsSelected = false

  endEditAirport () {
    this.toggle('airport')
    this.updateAvailableValues()
  }

  allAirportsToggleSelected (selected) {
    this.allAirportsSelected = selected
  }

  get selectedAirports () {
    return this.options.airport.filter(airport => airport.selected)
  }
  
  get selectedAirportLabel () {
    return this.getFormattedSelectLabel(this.selectedAirports, 'Wylot z...')
  }
  
  get selectedAirportValue () {
    return this.getFormattedSelectValue(this.selectedAirports, 'Wylot z...')
  }
  // Airports --------------

  // Calendar --------------
  editedDates = null // Needed to show temporary values as the user hovers over days

  startEditDates (mode) {
    if (this.dropdowns.dates) {
      if (this.options.dates.mode === mode) {
        this.toggle('dates')
      } else if (this.options.dates.mode !== 'date' || this.options.dates.mode !== 'month') {
        this.options.dates.mode = mode
      }
    } else {
      this.editedDates = moment.range(this.options.dates.selected.start, this.options.dates.selected.end)
      this.setDatesMode(mode)
      this.toggle('dates')
    }
  }

  endEditDates () {
    if (this.options.dates.mode === 'end') {
      // Prevents unnecessary Ajax request caused only by calendar mode change
      this.options.dates.mode = 'start'
    }
    this.toggle('dates')
    this.updateAvailableValues()
  }

  setDatesMode (mode) {
    if (this.options.dates.mode === 'date') this.options.dates.mode = 'date'
    else if (this.options.dates.mode === 'month') this.options.dates.mode = 'month'
    else this.options.dates.mode = mode
  }

  updateEditedDates (start, end) {
    this.editedDates = moment.range(start, end)
  }

  datesInitialState (start, end) {
    return this.options.dates.validDates.start.isSame(start, 'day') && this.options.dates.validDates.end.isSame(end, 'day')
  }

  invalidEndDateFallback (dates) {
    if (this.options.dates.validDates.contains(dates.end)) {
      return dates.end.format('dd DD.MM.YY')
    } else {
      return this.options.dates.validDates.end.format('dd DD.MM.YY')
    }
  }

  get selectedDateLabel () {
    return 'Termin wylotu'
  }

  get selectedDateStartValue () {
    const dates = this.dropdowns.dates ? this.editedDates : this.options.dates.selected
    if (this.options.dates.mode == 'month') {
      const formattedName = moment(dates.start).format('MMMM')
      const formattedYear = moment(dates.start).year()
      return `${formattedName} ${formattedYear}`
    } else if (this.datesInitialState(dates.start, dates.end)) {
      return `od ${dates.start.format('dd DD.MM.YYYY')}`
    } else {
      return `${dates.start.format('dd DD.MM.YY')} -`
    }
  }

  get selectedDateEndValue () {
    const dates = this.dropdowns.dates ? this.editedDates : this.options.dates.selected
    if (this.options.dates.mode === 'end' && dates.end.isZero()) {
      return this.options.dates.selected.end.format('dd DD.MM.YY')
    } else if (!this.datesInitialState(dates.start, dates.end) && this.options.dates.mode !== 'month') {
      return this.invalidEndDateFallback(dates)
    } else if (this.options.dates.validDates.end.isSame(dates.end, 'day') || dates.end.isZero()) {
      return ''
    } else if (this.options.dates.mode === 'month') {
      return ''
    } else {
      return dates.end.format('dd DD.MM.YY')
    }
  }
  // Calendar --------------

  // Duration --------------
  get selectedDurationLabel () {
    return 'Długość pobytu'  
  }

  get selectedDurationValue () {
    return `${this.options.duration.range.split(':').join('-')} dni`
  }
  // Duration --------------

  // People ----------------
  endEditPeople () {
    this.toggle('people')
    this.updateAvailableValues()
  }

  get selectedPeopleValue () {
    const adults = this.options.people.adults.value
    const children = this.options.people.children.value
    return `Dorośli ${adults} / Dzieci ${children}`
  }
  // People ----------------

  get selectedRoom () {
    return this.options.room.options
      .filter(room => room.value === this.options.room.selected)
      .firstOrDefault({ name: '' })
  }
  
  get selectedRoomLabel () {
    return this.getFormattedSelectLabel(this.selectedRoom.name, 'Pokoje')
  }
  
  get selectedRoomValue () {
    return this.getFormattedSelectValue(this.selectedRoom.name, 'Pokoje')
  }

  get selectedBoarding () {
    return this.options.boarding.options
      .filter(boarding => boarding.value === this.options.boarding.selected)
      .firstOrDefault({ name: '' })
  }
  
  get selectedBoardingLabel () {
    return this.getFormattedSelectLabel(this.selectedBoarding.name, 'Wyżywienie')
  }
  
  get selectedBoardingValue () {
    return this.getFormattedSelectValue(this.selectedBoarding.name, 'Wyżywienie')
  }

  get selectedPricePerPerson () {
    return this.options.pricePerPerson.options
      .filter(pricePerPerson => pricePerPerson.value === this.options.pricePerPerson.selected)
      .firstOrDefault({ name: '' })
  }

  get selectedPricePerPersonLabel () {
    return this.getFormattedSelectLabel(this.selectedPricePerPerson.name, 'Cena za osobę')
  }
  
  get selectedPricePerPersonValue () {
    return this.getFormattedSelectValue(this.selectedPricePerPerson.name, 'Cena za osobę')
  }
}
</script>

<style lang="scss">
.search-bar {
  display: grid;
  background-color: #005ca9;
  padding: 20px 28px 28px;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 30px;
  grid-row-gap: 10px;

  @include media(medium) {
    grid-template-columns: repeat(4, 1fr);
  }

  @include media(desktop) {
    grid-template-columns: 130px 210px 95px 167px repeat(3, 147px);
    grid-column-gap: 20px;
  }

  &__debugger {
    position: absolute;
    left: 25%;
    right: 25%;
    top: 50%;
    height: 50%;
  }

  &__input-box {
    font-family: 'Gotham Book';
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    text-transform: none;
    position: relative;
    color: #3c3939;
  }

  .input-box__label {
    color: hsla(0,0%,100%,.5);
  }
  .input-box__value {
    color: #fff;
  }

  .calendar-dropdown {
    position: absolute;
    margin-top: 4px;
  }
  
  .checklist-item {
    padding-bottom: 10px;

    &:last-child {
      padding-bottom: 0;
    }

    &__label {
      font-family: 'Gotham Book';
      font-weight: 400;
      font-size: 12px;
    }

    .checkbox-label--enabled {
      cursor: pointer;
    }

    .checkbox-label--disabled {
      cursor: default;
    }

    .checkbox-label--selected {
      font-family: Gotham;
      font-weight: 700;
    }
  }

  .radio-list-dropdown {
    min-width: 140px;
  }

  .radiolist-item__radiobutton {
    display: none;
  }

  .radiolist-item__label {
    padding-left: 0;

    &::before,
    &::after {
      display: none;
    }
  }

  .radiolist-item__label--selected {
    font-weight: bold;
    font-family: 'Gotham';
  }

  .airport-dropdown {
    width: 240px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Search.vue